import "./LabInformationAndContactDetailsEditComponent.scss";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import React, {useCallback, useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import CommonService from "../../../../../shared/services/common.service";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import {APIConfig} from "../../../../../constants";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import * as Yup from "yup";
import _ from "lodash";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";

interface LabInformationAndContactDetailsEditComponentProps {
    labDetails: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const labBasicInformationFormValidationSchema = Yup.object({
    name: Yup.string()
        .required('Lab Name is required'),
    labExternalId: Yup.string()
        .nullable()
        // .required('Lab ID is required')
        .max(20, "Lab ID should be Maximum 20 digits"),
    npiNumber: Yup.string()
        .nullable()
        .matches(/^[0-9]{10}$/, "Lab NPI Number should be 10 digits"),
    cliaId: Yup.string()
        .nullable()
        .required('CLIA ID is required')
        .matches(/^[A-Z0-9]{10}$/i, "CLIA ID should be 10 characters"),
    capId: Yup.string()
        .nullable()
        .matches(/^[0-9]{7}$/, "CAP ID should be 7 digits"),
    colaId: Yup.string().nullable()
        .matches(/^[0-9]{5}$/, "COLA ID should be 5 digits"),
    // labType: Yup.mixed()
    //     .nullable()
    //     .required('Lab Type is required'),
    emailId: Yup.string().nullable().required("Email ID is required")
        .email("Invalid Email ID")
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID"),
    mobileNumber: Yup.string().nullable().required("Mobile Number is required")
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    faxNumber: Yup.string().nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    secondaryMobileNumber: Yup.string()
        .nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    addressLine1: Yup.string().nullable()
        .required("Address 1 is required")
        .max(200, 'Address Line 1 cannot exceed 200 characters'),
    addressLine2: Yup.string().nullable().max(200, 'Address Line 2 cannot exceed 200 characters'),
    zipcode: Yup.mixed().required("Zip Code is required"),
    state: Yup.mixed().required("State is required"),
    city: Yup.mixed().required("City is required"),
    newZipCode: Yup.string().when('zipcode', {
        is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
        then: Yup.string().required('Zip Code is required').matches(/^[0-9]{5}$/, 'Enter a valid ZIP Code'),
    }),
})

interface LabInformationFormSchema {
    name: string,
    labExternalId: string,
    npiNumber: string,
    cliaId: string,
    capId: string,
    colaId: string,
    // labType: string,
    mobileNumber: string,
    secondaryMobileNumber: string,
    faxNumber: string;
    emailId: string,
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
}

const formInitialValues: LabInformationFormSchema = {
    name: '',
    labExternalId: '',
    npiNumber: '',
    cliaId: '',
    capId: '',
    colaId: '',
    // labType: '',
    mobileNumber: "",
    secondaryMobileNumber: "",
    faxNumber: "",
    emailId: "",
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
}

const LabInformationAndContactDetailsEditComponent = (props: LabInformationAndContactDetailsEditComponentProps) => {
    const {labDetails, onUpdateComplete, onEditCancel} = props;
    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);
    const [labInformationInitialValues, setLabInformationInitialValues] = useState<LabInformationFormSchema>(_.cloneDeep(formInitialValues));

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("state", "");
            setFieldValue("city", "");
            // setFieldValue("timezone", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("state", "");
                setFieldValue("city", "");
                // setFieldValue("timezone", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("state", zipCodeObject.state);
                setFieldValue("city", zipCodeObject.city);
                // setFieldValue("timezone", zipCodeObject.timezone);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    useEffect(() => {
        if (labDetails) {
            const labDetailsObj = _.cloneDeep(labDetails);
            if (!labDetails.zipcodeDetails) {
                labDetailsObj.newZipCode = labDetailsObj?.zipcode;
                labDetailsObj.zipcode = {
                    zipcode: "Other"
                };
                labDetailsObj.state = labDetailsObj?.stateDetails;
                labDetailsObj.city = labDetailsObj?.cityDetails;
                setIsZipCodeNotFound(true);
            } else {
                labDetailsObj.zipcode = labDetailsObj.zipcodeDetails;
                setIsZipCodeNotFound(false);
            }
            labDetailsObj.mobileNumber = CommonService.getMaskedMobileNumber(labDetailsObj?.mobileNumber);
            labDetailsObj.secondaryMobileNumber = CommonService.getMaskedMobileNumber(labDetailsObj?.secondaryMobileNumber)
            setState(labDetailsObj.state);
            setLabInformationInitialValues(_.cloneDeep(labDetailsObj));
        }
    }, [labDetails]);

    const onFormSubmit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = _.cloneDeep(values);
        delete payload["directorDetails"];
        if (payload["newZipCode"]) {
            payload["zipcode"] = payload["newZipCode"];
            payload["state"] = payload?.state?.state;
            payload["city"] = payload?.city?.city;
            delete payload["newZipCode"];
        } else {
            payload["zipcode"] = payload?.zipcode?.zipcode;
        }
        payload["mobileNumber"] = payload["mobileNumber"]?.replace(/[^0-9]/g, "");
        payload["secondaryMobileNumber"] = payload["secondaryMobileNumber"]?.replace(/[^0-9]/g, "");
        setSubmitting(true);
        CommonService._lab.editLabDetails(labDetails.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                CommonService._alert.showToast(response?.message || 'Lab details has been added', "success");
                onUpdateComplete(response.data);
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
                CommonService._alert.showToast(error.error||"Error while updating Lab","error")
            })

    }, [labDetails, onUpdateComplete]);

    return (
        <div className="lab-information-component details-tab-data-content">
            <Formik
                validationSchema={labBasicInformationFormValidationSchema}
                initialValues={labInformationInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                onSubmit={onFormSubmit}
            >
                {({isSubmitting, values, isValid, errors, setFieldValue, validateForm, dirty}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form className={"lab-information-holder"}>
                            <div className={'t-form-controls-wrapper'}>
                                <div className="form-sub-title">Lab Details</div>
                                <Field name={'name'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Lab Name'}
                                                placeholder={'Enter Lab Name'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'labExternalId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Lab ID'}
                                                        placeholder={'Enter Lab ID'}
                                                        type={"text"}
                                                        maxLength={20}
                                                        // required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'npiNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Lab NPI Number'}
                                                        placeholder={'Enter Lab NPI Number'}
                                                        type={"text"}
                                                        maxLength={10}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'cliaId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'CLIA ID'}
                                                        placeholder={'Enter CLIA ID'}
                                                        maxLength={10}
                                                        type={"text"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'capId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'CAP ID'}
                                                        placeholder={'Enter CAP ID'}
                                                        type={"text"}
                                                        maxLength={7}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'colaId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'COLA ID'}
                                                        placeholder={'Enter COLA ID'}
                                                        type={"text"}
                                                        maxLength={5}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    {/*<Grid item xs={12} sm={12} md={6} xl={6}>*/}
                                    {/*    <Field name={'labType'}>*/}
                                    {/*        {*/}
                                    {/*            (field: FieldProps) => (*/}
                                    {/*                <FormikSelectDropdownComponent*/}
                                    {/*                    label={'Lab Type'}*/}
                                    {/*                    placeholder={'Select Lab Type'}*/}
                                    {/*                    required={true}*/}
                                    {/*                    valueKey={'code'}*/}
                                    {/*                    options={CommonService._staticData.labType}*/}
                                    {/*                    formikField={field}*/}
                                    {/*                    getOptionLabel={(option => (option.title) || '')}*/}

                                    {/*                />*/}
                                    {/*            )*/}
                                    {/*        }*/}
                                    {/*    </Field>*/}
                                    {/*</Grid>*/}
                                </Grid>
                                <div className={'horizontal-line'}></div>
                                <div className="form-sub-title">Contact Details</div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'emailId'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Email ID'}
                                                        placeholder={'Enter Email ID'}
                                                        type={"email"}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'mobileNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FromikPhoneNumberComponent
                                                        label={'Mobile Number'}
                                                        placeholder={'(000) 000-0000'}
                                                        maxLength={14}
                                                        required={true}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'secondaryMobileNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FromikPhoneNumberComponent
                                                        label={'Secondary Mobile Number'}
                                                        placeholder={'(000) 000-0000'}
                                                        maxLength={14}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'faxNumber'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FromikPhoneNumberComponent
                                                        label={'Fax Number'}
                                                        placeholder={'(000) 000-0000'}
                                                        maxLength={14}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <div className={'horizontal-line'}></div>
                                <div className="form-sub-title">Address Details</div>
                                <Field name={'addressLine1'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Address Line 1'}
                                                placeholder={'Enter Address Line 1'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'addressLine2'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Address Line 2'}
                                                        placeholder={'Enter Address Line 2'}
                                                        type={"text"}
                                                        required={false}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'zipcode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'ZIP Code'}
                                                        placeholder={'Start typing for ZIP Code'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        openOnFocus={true}
                                                        url={APIConfig.ZIPCODE_LIST.URL}
                                                        method={APIConfig.ZIPCODE_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        data={[]}
                                                        valueKey={'zipcode'}
                                                        dataListKey={"data.docs"}
                                                        onUpdate={(value: any) => {
                                                            handleZipCodeValueChange(value, setFieldValue)
                                                        }}
                                                        //Hiding other option from zipcode
                                                        // defaultData={[
                                                        //     {
                                                        //         zipcode: "Other"
                                                        //     }
                                                        // ]}
                                                        getOptionLabel={(option => (option.zipcode) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                {
                                    isZipCodeNotFound && <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'newZipCode'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                placeholder={"Enter New ZIP Code"}
                                                                label={'New ZIP Code'}
                                                                required={true}
                                                                maxLength={5}
                                                                type={"text"}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>

                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'state'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikAutoCompleteDropdown
                                                                label={'State'}
                                                                required={true}
                                                                formikField={field}
                                                                url={APIConfig.STATE_LIST.URL}
                                                                method={APIConfig.STATE_LIST.METHOD}
                                                                searchMode={'serverSide'}
                                                                openOnFocus={true}
                                                                dataListKey={"data.docs"}
                                                                data={[]}
                                                                valueKey={'state'}
                                                                onUpdate={(value: any) => {
                                                                    setState(value?.state);
                                                                    field.form.setFieldValue("city", '');
                                                                }}
                                                                getOptionLabel={(option => (option.state) || '')}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'city'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikAutoCompleteDropdown
                                                                label={'City'}
                                                                placeholder={'City'}
                                                                required={true}
                                                                id="city_auto_complete"
                                                                formikField={field}
                                                                searchMode={'serverSide'}
                                                                url={APIConfig.CITY_LIST.URL}
                                                                method={APIConfig.CITY_LIST.METHOD}
                                                                valueKey={'city'}
                                                                disabled={!state}
                                                                payload={{
                                                                    state: state,
                                                                    page: 1,
                                                                    limit: 100
                                                                }}
                                                                data={[]}
                                                                getOptionLabel={(option => (option.city) || '')}
                                                                dataListKey={"data.docs"}
                                                            />
                                                        )
                                                    }
                                                </Field>

                                            </Grid>
                                            <Grid item xs={12} sm={6}>

                                            </Grid>
                                        </Grid></>
                                }
                                {
                                    !isZipCodeNotFound && values?.zipcode?.zipcode && values?.zipcode?.zipcode !== "Other" &&
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'State'}
                                                            placeholder={'Enter state'}
                                                            required={true}
                                                            readOnly={true}
                                                            formikField={field}
                                                            type={"text"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'City'}
                                                            placeholder={'Enter city'}
                                                            required={true}
                                                            readOnly={true}
                                                            formikField={field}
                                                            type={"text"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                }
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    );

};

export default LabInformationAndContactDetailsEditComponent;
